import { defineStore } from "pinia";
import localforage from "localforage";

import type { Evidence, ThemeUsageType } from "@/models/Evidence";
import { type EvidenceTheme } from "@/models/EvidenceTheme";

import { evidenceService } from "@/services";

import { useAnnotationStore, useBrandStore, useCaseStore } from "@/stores";

import type { ApiError } from "@/services/Api";

const keyMethod = (keys: string[]) => {
  return keys.map((e) => (e ? e.toLowerCase() : "")).join("-");
};

function findEvidence(evidences: readonly Evidence[], item: Evidence) {
  return evidences.find(
    (e) =>
      e.fileReference === item.fileReference &&
      e.theme === item.theme &&
      e.type === item.type &&
      String(e.value) === String(item.value),
  );
}

export const useEvidenceStore = defineStore("evidence", {
  scope: "case",
  state: (): State => ({
    categories: [],
    evidence: [],
    loadingCategories: true,
    loadingEvidence: true,
  }),
  getters: {
    loading(): boolean {
      return this.loadingCategories || this.loadingEvidence;
    },
    bookmarkCategories(): EvidenceTheme[] {
      return this.categories.filter((c) => c.usageTypes.has("bookmark"));
    },
    categoriesWithEvidences(): Array<{ name: string; usageTypes: Set<ThemeUsageType>; entries: Evidence[] }> {
      const evidenceByCategory = this.evidence.reduce(
        (acc, evidence) => {
          const key = evidence.theme.toLowerCase();

          acc[key] ??= [];
          acc[key].push(evidence);
          // Default this because it has been marked as evidence
          evidence.source = "user";

          return acc;
        },
        useAnnotationStore().annotations.reduce(
          (acc, { evidenceThemes, fileReference, markId, source }) => {
            // Filter out accepted to avoid duplicates.
            if (evidenceThemes != null && !/user/i.test(source)) {
              for (const { evidenceTheme, status } of evidenceThemes) {
                if (/accepted/i.test(status ?? "")) continue;

                const key = evidenceTheme.toLowerCase();
                acc[key] ??= [];
                acc[key].push({
                  status,
                  fileReference,
                  theme: evidenceTheme,
                  type: "annotation",
                  value: markId,
                  displayName: markId,
                  source: source,
                });
              }
            }

            return acc;
          },
          {} as Record<string, Evidence[]>,
        ),
      );

      return this.categories.map((c) => ({
        name: c.name,
        usageTypes: c.usageTypes,
        entries: evidenceByCategory[c.name.toLowerCase()] ?? [],
      }));
    },
    availableCategoriesWithEvidences(): Array<{ name: string; usageTypes: Set<ThemeUsageType>; entries: Evidence[] }> {
      return this.categoriesWithEvidences.filter((c) => c.entries.length > 0);
    },
  },
  actions: {
    async loadCategories(): Promise<EvidenceTheme[]> {
      const brandStore = useBrandStore();
      if (brandStore.featuresConfig?.evidence === "disabled") return [];

      const caseReference = useCaseStore().selectedCaseName;
      if (brandStore.selectedClient == null || brandStore.selectedBrand == null || caseReference == null) return [];

      this.setCategoriesLoading({ isLoading: true });
      const sortedThemes = (
        await evidenceService.getCategories(brandStore.selectedClient, brandStore.selectedBrand)
      ).sort((a, b) => a.name.localeCompare(b.name));

      const themes = sortedThemes.reduce((acc, theme) => {
        // sorted by name, so the last item having a different name means it's a new group
        const existing = acc[acc.length - 1];

        if (existing?.name === theme.name) {
          // Combine the usage types from all categories with the same name
          existing.usageTypes.add(theme.usage);
        } else {
          theme.usageTypes = new Set<ThemeUsageType>([theme.usage]);
          acc.push(theme);
        }
        return acc;
      }, [] as EvidenceTheme[]);

      this.categoriesLoaded({ categories: themes });
      await localforage.setItem(keyMethod([brandStore.selectedBrand]), themes);

      return themes;
    },
    async loadEvidence(isRefreshing?: boolean): Promise<Evidence[]> {
      const brandStore = useBrandStore();
      if (brandStore.featuresConfig?.evidence === "disabled") return [];

      const caseReference = useCaseStore().selectedCaseName;
      if (brandStore.selectedClient == null || brandStore.selectedBrand == null || caseReference == null) return [];

      if (isRefreshing !== true) this.setEvidenceLoading({ isLoading: true });

      const evidence = await evidenceService.getEvidence(
        brandStore.selectedClient,
        brandStore.selectedBrand,
        caseReference,
      );
      this.evidenceLoaded(evidence || []);
      await localforage.setItem(keyMethod([brandStore.selectedBrand, caseReference]), evidence);

      return evidence;
    },
    async addEvidence(payload: Evidence): Promise<void> {
      // Ensure there are no dupes
      if (findEvidence(this.evidence, payload) != null) {
        const error: ApiError = {
          status: 400,
          statusText: "Duplicate",
        };
        throw error;
      }

      const brandStore = useBrandStore();
      if (brandStore.featuresConfig?.evidence === "disabled") return;

      const caseReference = useCaseStore().selectedCaseName;
      if (brandStore.selectedClient == null || brandStore.selectedBrand == null || caseReference == null) return;

      await evidenceService.addEvidence(brandStore.selectedClient, brandStore.selectedBrand, caseReference, payload);

      await this.loadEvidence(true);
      this.triggerPolling();
    },
    async renameEvidence(payload: { annotation: string; item: Evidence }) {
      const brandStore = useBrandStore();
      if (brandStore.featuresConfig?.evidence === "disabled") return;

      const caseReference = useCaseStore().selectedCaseName;
      if (brandStore.selectedClient == null || brandStore.selectedBrand == null || caseReference == null) return;

      const item = findEvidence(this.evidence, payload.item);
      if (item == null) return;

      const currentTitle = item.annotation;

      try {
        // Optimistically update
        item.annotation = payload.annotation;

        const response = (await evidenceService.renameEvidence(
          brandStore.selectedClient,
          brandStore.selectedBrand,
          caseReference,
          payload.item.fileReference,
          payload.item.theme,
          payload.item.type,
          payload.item.value,
          payload.annotation,
        )) as { status: number } | string;

        if (typeof response === "object" && response.status !== 200) throw response;
      } catch (error) {
        // Revert to Original
        item.annotation = currentTitle;
        throw error;
      } finally {
        await this.loadEvidence(true);
      }
    },

    async deleteEvidence(payload: Evidence): Promise<void> {
      const brandStore = useBrandStore();
      if (brandStore.featuresConfig?.evidence === "disabled") return;

      const caseReference = useCaseStore().selectedCaseName;
      if (brandStore.selectedClient == null || brandStore.selectedBrand == null || caseReference == null) return;

      await evidenceService.deleteEvidence(
        brandStore.selectedClient,
        brandStore.selectedBrand,
        caseReference,
        payload.fileReference,
        payload.theme,
        payload.type,
        payload.value,
      );
      await this.loadEvidence(true);
      this.triggerPolling();
    },

    // Mutations
    setCategoriesLoading(payload: { isLoading: boolean }): void {
      this.loadingCategories = payload.isLoading;
    },
    setEvidenceLoading(payload: { isLoading: boolean }): void {
      this.loadingEvidence = payload.isLoading;
    },
    evidenceLoaded(payload: Evidence[]): void {
      this.evidence = Object.freeze(payload);
      this.setEvidenceLoading({ isLoading: false });
    },
    categoriesLoaded(payload: { categories: EvidenceTheme[] }): void {
      this.categories = Object.freeze(payload.categories);
      this.setCategoriesLoading({ isLoading: false });
    },
    reset(): void {
      this.categories = [];
      this.evidence = [];
      this.loadingCategories = true;
      this.loadingEvidence = true;
    },
    triggerPolling() {
      // Do nothing. Placeholder to reset polling plugin tier
    },
  },
});

interface State {
  categories: readonly EvidenceTheme[];
  evidence: readonly Evidence[];
  loadingCategories: boolean;
  loadingEvidence: boolean;
}
